/* ============================================================
   RVRN Case — shared behaviour
   - Idempotent: each .rvrn-case root initialises at most once.
   - Editor-safe: re-initialises on shopify:section:load.
   - Accessible: timeline & solution are click + keyboard (Enter/Space),
     not hover-only. Mobile timeline becomes a real accordion.
   - Honours prefers-reduced-motion.
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Reveal on scroll ---------- */
  function initReveal(root) {
    var reveals = root.querySelectorAll('.rvrn-case__reveal');
    if (!reveals.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      reveals.forEach(function (el) { el.classList.add('rvrn-case__reveal--visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('rvrn-case__reveal--visible');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.15 });
    reveals.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Standard carousel (scroll-based, like the row carousel) ---------- */
  function initCarousel(root) {
    if (reduceMotion) return;
    var carousels = root.querySelectorAll('[data-carousel]:not([data-carousel-row])');
    carousels.forEach(function (carousel) {
      var track = carousel.querySelector('.rvrn-case__carousel-track');
      if (!track || track.dataset.rvCarInit === 'true') return;
      track.dataset.rvCarInit = 'true';

      var total = track.children.length;
      var dots = carousel.querySelector('.rvrn-case__carousel-dots');
      var autoplay = carousel.getAttribute('data-autoplay') === 'true';
      var speed = parseInt(carousel.getAttribute('data-speed'), 10) || 5;

      if (total <= 1) {
        carousel.classList.add('rvrn-case__carousel--no-nav');
        return;
      }

      /* One slide == one track width (flex: 0 0 100%); step = slide + gap. */
      function step() {
        var slide = track.querySelector('.rvrn-case__carousel-slide');
        if (!slide) return track.clientWidth || 1;
        var cs = getComputedStyle(track);
        var gap = parseFloat(cs.columnGap || cs.gap || '0') || 0;
        return slide.getBoundingClientRect().width + gap;
      }
      function indexFromScroll() {
        var s = step() || 1;
        return Math.round(track.scrollLeft / s);
      }

      /* Build dots (one per slide). */
      if (dots) {
        for (var i = 0; i < total; i++) {
          (function (idx) {
            var dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'rvrn-case__carousel-dot';
            dot.setAttribute('aria-label', 'Go to slide ' + (idx + 1));
            dot.addEventListener('click', function () { go(idx); });
            dots.appendChild(dot);
          })(i);
        }
      }

      function render() {
        if (!dots) return;
        var idx = indexFromScroll();
        Array.prototype.forEach.call(dots.children, function (d, i) {
          d.classList.toggle('rvrn-case__is-active', i === idx);
          d.setAttribute('aria-current', i === idx ? 'true' : 'false');
        });
      }
      /* Navigate by scrolling the track (not transform) so wheel + arrows stay in sync. */
      function go(i) {
        var idx = (i + total) % total;
        track.scrollTo({ left: idx * step(), behavior: 'smooth' });
        restart();
      }

      var timer = null;
      function restart() {
        if (!autoplay) return;
        if (timer) clearInterval(timer);
        timer = setInterval(function () { go(indexFromScroll() + 1); }, speed * 1000);
      }

      var prev = carousel.querySelector('.rvrn-case__carousel-prev');
      var next = carousel.querySelector('.rvrn-case__carousel-next');
      if (prev) prev.addEventListener('click', function () { go(indexFromScroll() - 1); });
      if (next) next.addEventListener('click', function () { go(indexFromScroll() + 1); });

      track.addEventListener('scroll', render, { passive: true });
      window.addEventListener('resize', render);
      render();
      restart();
    });
  }

  /* ---------- Edge-to-edge "row" carousel ---------- */
  function initRowCarousel(root) {
    if (reduceMotion) return;
    var rows = root.querySelectorAll('[data-carousel-row]');
    rows.forEach(function (row) {
      var track = row.querySelector('.rvrn-case__carousel-track');
      if (!track || track.dataset.rvRowInit === 'true') return;
      track.dataset.rvRowInit = 'true';

      var dots = row.parentElement.querySelector('.rvrn-case__carousel-dots');

      function step() {
        var slide = track.querySelector('.rvrn-case__carousel-slide');
        if (!slide) return 280;
        var cs = getComputedStyle(track);
        var gap = parseFloat(cs.columnGap || cs.gap || '16') || 16;
        return slide.getBoundingClientRect().width + gap;
      }
      function maxIndex() {
        return Math.max(0, track.scrollWidth - track.clientWidth);
      }
      function render() {
        if (dots) {
          var max = maxIndex();
          var idx = Math.round(track.scrollLeft / (step() || 1));
          Array.prototype.forEach.call(dots.children, function (d, i) {
            d.classList.toggle('rvrn-case__is-active', i === idx);
            d.setAttribute('aria-current', i === idx ? 'true' : 'false');
          });
          if (track.scrollLeft >= max - 2) {
            row.classList.add('rvrn-case__is-end');
          } else {
            row.classList.remove('rvrn-case__is-end');
          }
        }
      }
      var prev = row.parentElement.querySelector('.rvrn-case__carousel-prev');
      var next = row.parentElement.querySelector('.rvrn-case__carousel-next');
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
      track.addEventListener('scroll', render, { passive: true });
      window.addEventListener('resize', render);
      render();
    });
  }

  /* ---------- Solution cards: click + keyboard (no hover-only) ---------- */
  function initSolution(root) {
    var grids = root.querySelectorAll('.rvrn-case__solution-grid');
    grids.forEach(function (grid) {
      if (grid.dataset.rvSolInit === 'true') return;
      grid.dataset.rvSolInit = 'true';

      var cards = Array.prototype.slice.call(grid.querySelectorAll('.rvrn-case__solution-card'));
      if (!cards.length) return;

      function setActive(card) {
        cards.forEach(function (c) {
          var on = c === card;
          c.classList.toggle('rvrn-case__is-active', on);
          c.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      }

      cards.forEach(function (card) {
        card.setAttribute('role', 'button');
        card.setAttribute('tabindex', '0');
        card.addEventListener('click', function () { setActive(card); });
        card.addEventListener('mouseenter', function () { setActive(card); });
        card.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            setActive(card);
          }
        });
      });

      setActive(cards[0]);
    });
  }

  /* ---------- Timeline: hover/focus reveal (CSS-driven; a11y attrs only) ---------- */
  function initTimeline(root) {
    var timelines = root.querySelectorAll('.rvrn-case__timeline');
    timelines.forEach(function (tl) {
      if (tl.dataset.rvTlInit === 'true') return;
      tl.dataset.rvTlInit = 'true';
      var items = tl.querySelectorAll('.rvrn-case__timeline-item');
      Array.prototype.forEach.call(items, function (it) {
        it.setAttribute('role', 'button');
        it.setAttribute('tabindex', '0');
      });
    });
  }

  /* ---------- Per-root idempotent init ---------- */
  function initRvCaseRoot(root) {
    if (!root || root.dataset.rvCaseInit === 'true') return;
    root.dataset.rvCaseInit = 'true';
    root.classList.add('rvrn-case--js');
    initReveal(root);
    initCarousel(root);
    initRowCarousel(root);
    initSolution(root);
    initTimeline(root);
  }

  function initAll() {
    document.querySelectorAll('.rvrn-case').forEach(initRvCaseRoot);
  }

  function onReady() {
    initAll();
    document.addEventListener('shopify:section:load', function (e) {
      initRvCaseRoot(e.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
